import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import {
  TicketingFormCategory,
  getField,
  getIsoLocaleFromCountry,
  getTicketingLink,
  OrganizationRoutes,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { stringify } from "query-string";

import { AmplitudeEvents } from "../constants/amplitude";
import { useCurrentUserContext } from "../contexts/CurrentUserContext";
import { useLocaleContext } from "../contexts/LocaleContext";
import { GetCurrentUserFrontendObject } from "../contexts/types";
import { catchCancelledPush } from "../helpers/router";
import { useAmplitude } from "../hooks/amplitude/useAmplitude";
import { useIsOnboarding } from "../hooks/useIsOnboarding";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { FormRef } from "../types/formCreation";

import { DonationFormStepName, TicketingStepName } from "@/constants/Steppers";
import { DonationFormCategory, DonationFormObject } from "@/gql-types";
import { trpc } from "@/helpers/trpc";
import { useWarnIfUnsavedChanges } from "@/hooks/useWarnIfUnsavedChanges";
import { TicketingOutput } from "@/types/trpc";

// Base interface for common properties
export interface BaseFormEditionProps {
  isEditing: boolean;
  nextStep: () => void;
  prevStep: () => void;
  hasPassedFirstStep: boolean;
  isOnboarding: boolean;
  submitIsLoading: boolean;
  formTitle?: string;
  formOrganization?: GetCurrentUserFrontendObject["currentOrganization"];
  formIsDirty: boolean;
  setFormIsDirty: (value: boolean) => void;
  displayPreviewButtonBadge: boolean;
  setDisplayPreviewButtonBadge: (value: boolean) => void;
  displaySmallScreenEditDropdownButtonBadge: boolean;
  setDisplaySmallScreenEditDropdownButtonBadge: (value: boolean) => void;
  setSubmitIsLoading: (value: boolean) => void;
  handleGoBack: (_: { onSuccess?: () => void; onError?: () => void }) => Promise<void>;
  handleDeleteDraftForm: () => Promise<void>;
  handleSaveForm: (_: { onSuccess?: () => void; onError?: () => void }) => Promise<void>;
  handleSubmit: () => Promise<void>;
  setHashStep: (step: string) => void;
  frontendLink?: string;
  isVisibleTranslations: boolean;
  isShareModalOpen: boolean;
  openShareModal: () => void;
  closeShareModal: () => void;
  logAmplitudeOnSubmitSuccess: () => void;
  logAmplitudeOnSubmitFailure: (error: string) => () => void;
  eventFirstStartDate?: Date;
}

// Specific interface for DonationForm
export interface DonationFormFormEditionProps extends BaseFormEditionProps {
  data: DonationFormObject;
  isFundraiserEditionFlow: "public" | "private" | false;
  currentStep: DonationFormStepName;
  formCategory: DonationFormCategory;
  refetch: () => Promise<void>;
}

// Specific interface for Ticketing
export interface TicketingFormEditionProps extends BaseFormEditionProps {
  data: TicketingOutput;
  isFundraiser: boolean;
  isAuction: boolean;
  currentStep: TicketingStepName;
  formCategory: TicketingFormCategory;
  refetchTicketing: () => Promise<void>;
}

export interface TicketingEditionContentProps {
  data: TicketingOutput;
  refetchTicketing: () => Promise<void>;
}

// Default values for Ticketing
const ticketingDefaultValues: TicketingFormEditionProps = {
  isEditing: false,
  data: {} as TicketingOutput,
  refetchTicketing: async () => {},
  isFundraiser: false,
  isAuction: false,
  isOnboarding: false,
  nextStep: () => {},
  prevStep: () => {},
  hasPassedFirstStep: false,
  submitIsLoading: false,
  formOrganization: {} as GetCurrentUserFrontendObject["currentOrganization"],
  formIsDirty: false,
  setFormIsDirty: () => {},
  displayPreviewButtonBadge: false,
  setDisplayPreviewButtonBadge: () => {},
  displaySmallScreenEditDropdownButtonBadge: false,
  setDisplaySmallScreenEditDropdownButtonBadge: () => {},
  setSubmitIsLoading: () => {},
  handleGoBack: async () => {},
  handleDeleteDraftForm: async () => {},
  handleSaveForm: async () => {},
  currentStep: TicketingStepName.GeneralInformations,
  handleSubmit: async () => {},
  setHashStep: () => {},
  frontendLink: "",
  isVisibleTranslations: false,
  isShareModalOpen: false,
  openShareModal: () => {},
  closeShareModal: () => {},
  formCategory: TicketingFormCategory.Event,
  logAmplitudeOnSubmitSuccess: () => {},
  logAmplitudeOnSubmitFailure: () => () => {},
  formTitle: undefined,
  eventFirstStartDate: undefined,
};

export const TicketingFormEditionContext = createContext<TicketingFormEditionProps>(ticketingDefaultValues);

interface TicketingFormEditionProviderProps extends TicketingEditionContentProps {
  formRef: React.MutableRefObject<FormRef | null>;
}

export const TicketingFormEditionProvider = ({
  children,
  data,
  refetchTicketing,
  formRef,
}: PropsWithChildren<TicketingFormEditionProviderProps>) => {
  const router = useRouter();
  const { isSmallScreen } = useMediaQuery();
  const { locale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { organization } = useCurrentUserContext();
  const isEditing = router.pathname.includes("/edit");

  const { isOnboarding } = useIsOnboarding();

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [displayPreviewButtonBadge, setDisplayPreviewButtonBadge] = useState(false);
  const [displaySmallScreenEditDropdownButtonBadge, setDisplaySmallScreenEditDropdownButtonBadge] = useState(false);
  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [hasPassedFirstStep, setHasPassedFirstStep] = useState(false);
  const { mutateAsync: deleteTicketing } = trpc.deleteTicketing.useMutation();

  const ticketingStepNames = Object.values(TicketingStepName);

  const stepNames = useMemo(
    () => (isOnboarding ? ticketingStepNames : ticketingStepNames.filter((step) => step !== TicketingStepName.Review)),
    [isOnboarding, ticketingStepNames]
  );

  // log amplitude event first load
  useEffect(() => {
    logAmplitudeEvent(AmplitudeEvents.FormEditionStarted, {
      form_type: data.formCategory,
      is_onboarding: isOnboarding,
      is_new_form: !isEditing,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetSubmitIsLoading = useCallback((value: boolean) => {
    setSubmitIsLoading(value);
  }, []);

  const firstStep = TicketingStepName.GeneralInformations;
  const currentStep = location.hash.substr(1) as TicketingStepName;

  const formCategory = data.formCategory;

  const isAuction = formCategory === TicketingFormCategory.Auction;
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const currentUrl = `${router.pathname}?${stringify(query)}`;
  const currentIndex = stepNames.findIndex((stepName) => currentStep === stepName);

  const nextStep = useCallback(() => {
    const url = `${currentUrl}#${stepNames[currentIndex + 1]}`;
    setHasPassedFirstStep(true);
    catchCancelledPush(router.push(url));
    setDisplaySmallScreenEditDropdownButtonBadge(false);
  }, [currentIndex, currentUrl, router, stepNames]);

  const prevStep = useCallback(() => {
    const url = `${currentUrl}#${stepNames[currentIndex - 1]}`;
    if (currentIndex > 0) {
      setHasPassedFirstStep(true);
    }
    catchCancelledPush(router.push(url));
    setDisplaySmallScreenEditDropdownButtonBadge(false);
  }, [currentIndex, currentUrl, router, stepNames]);

  const setHashStep = useCallback(
    (step: string) => {
      logAmplitudeEvent(AmplitudeEvents.FormStepChange, {
        current_step: currentStep,
        form_type: formCategory,
        is_new_form: !isEditing,
        is_onboarding: isOnboarding,
        target_step: step,
      });
      setHasPassedFirstStep(true);
      const url = `${currentUrl}#${step}`;
      catchCancelledPush(router.push(url));
    },
    [currentStep, currentUrl, formCategory, isEditing, isOnboarding, logAmplitudeEvent, router]
  );

  const isVisibleTranslations = Boolean(data.ticketingFields?.length) || currentStep === TicketingStepName.Translations;

  const frontendLink = useMemo(() => {
    const formLocale = isVisibleTranslations ? locale : data.locale;
    const openingLocale = getIsoLocaleFromCountry(organization?.country, formLocale || locale);
    return data?.id ? getTicketingLink(data.path, openingLocale) : undefined;
  }, [data, isVisibleTranslations, locale, organization?.country]);

  const handleSubmit = useCallback(async () => {
    setSubmitIsLoading(true);
    await formRef?.current?.handleSubmit?.({
      onSuccess: async () => {
        await refetchTicketing();
        setSubmitIsLoading(false);
        setFormIsDirty(false);
        setDisplaySmallScreenEditDropdownButtonBadge(true);
      },
      onError: () => {
        setSubmitIsLoading(false);
      },
    });
  }, [formRef, refetchTicketing]);

  const logAmplitudeOnSubmitSuccess = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.FormStepSubmitted, {
      form_type: formCategory,
      is_new_form: !isEditing,
      is_onboarding: isOnboarding,
      step: currentStep,
      errors: null,
    });
  }, [currentStep, formCategory, isEditing, isOnboarding, logAmplitudeEvent]);

  const logAmplitudeOnSubmitFailure = useCallback(
    (error: string) => () => {
      logAmplitudeEvent(AmplitudeEvents.FormStepSubmitted, {
        form_type: formCategory,
        is_new_form: !isEditing,
        is_onboarding: isOnboarding,
        step: currentStep,
        errors: error,
      });
    },
    [currentStep, formCategory, isEditing, isOnboarding, logAmplitudeEvent]
  );

  useWarnIfUnsavedChanges(formIsDirty);

  useEffect(() => {
    // redirect to first step if user is not on the right step
    const stepIndex = stepNames.indexOf(location.hash.substr(1) as TicketingStepName);

    // chatbot is not hidden on mobile
    const needHubspotChatbot = isSmallScreen && !searchParams.get("hideHubspotChatbot");

    // no donationFormId in edition flow
    const ticketingId = searchParams.get("ticketingId");
    const needCreationModeRedirection = isEditing && (!ticketingId || ticketingId === "undefined");

    // step name is not in the list || prev step was not completed in creation flow
    const needFirstStepRedirection = stepIndex === -1;

    const pathname = needCreationModeRedirection ? OrganizationRoutes.TicketingNew : router.pathname;
    const params = { ...query, ...(needHubspotChatbot && { hideHubspotChatbot: true }) };
    const step = needFirstStepRedirection ? firstStep : location.hash.substr(1);

    if (needHubspotChatbot || needCreationModeRedirection || needFirstStepRedirection) {
      router.replace(`${pathname}?${stringify(params)}#${step}`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteDraftForm = useCallback(async () => {
    await deleteTicketing({ ticketingId: data.id, onlyDraft: true });
  }, [data.id, deleteTicketing]);

  const handleSaveForm = useCallback(
    async ({ onSuccess, onError }: { onSuccess?: () => void; onError?: () => void }) => {
      await formRef?.current?.handleSaveForm?.({
        onSuccess: () => {
          // We call the passed onSuccess function
          onSuccess?.();
          // We refetch the data to update the form
          refetchTicketing();
        },
        onError,
      });
    },
    [formRef, refetchTicketing]
  );

  const handleGoBack = useCallback(
    async ({ onSuccess, onError }: { onSuccess?: () => void; onError?: () => void }) => {
      logAmplitudeEvent(AmplitudeEvents.FormStepChange, {
        current_step: currentStep,
        form_type: formCategory,
        is_new_form: !isEditing,
        is_onboarding: isOnboarding,
        target_step: null,
      });
      if (currentStep === TicketingStepName.Review) {
        prevStep();
      }
      await formRef?.current?.handleSaveForm?.({
        onSuccess: () => {
          // We call the passed onSuccess function
          onSuccess?.();
          prevStep();
          // We refetch the data to update the form
          refetchTicketing();
        },
        onError,
      });
    },
    [currentStep, formCategory, formRef, isEditing, isOnboarding, logAmplitudeEvent, prevStep, refetchTicketing]
  );

  const openShareModal = useCallback(() => setIsShareModalOpen(true), [setIsShareModalOpen]);
  const closeShareModal = useCallback(() => {
    setIsShareModalOpen(false);
    const shouldOpenShareModal = searchParams.get("shouldOpenShareModal");
    if (shouldOpenShareModal === "true") {
      const params = stringify({ ...query, shouldOpenShareModal: undefined });
      router.replace(`${router.pathname}?${params}#${currentStep}`);
    }
  }, [currentStep, query, router, searchParams]);

  // Return startUtc of the earliest occurrence
  const eventFirstStartDate = useMemo(() => {
    if (!data.occurrences?.length) {
      return undefined;
    }

    const dates = data.occurrences.map((occurrence) => new Date(occurrence.startUtc).getTime());
    return new Date(Math.min(...dates));
  }, [data.occurrences]);

  return (
    <TicketingFormEditionContext.Provider
      value={{
        isEditing,
        data,
        formTitle: getField(data.ticketingFields, data.locale)?.title,
        refetchTicketing,
        isOnboarding,
        submitIsLoading,
        formIsDirty,
        setFormIsDirty,
        displayPreviewButtonBadge,
        setDisplayPreviewButtonBadge,
        displaySmallScreenEditDropdownButtonBadge,
        setDisplaySmallScreenEditDropdownButtonBadge,
        setSubmitIsLoading: handleSetSubmitIsLoading,
        handleGoBack,
        handleDeleteDraftForm,
        handleSaveForm,
        currentStep,
        nextStep,
        prevStep,
        hasPassedFirstStep: hasPassedFirstStep || currentIndex > 0,
        isVisibleTranslations,
        handleSubmit,
        setHashStep,
        frontendLink,
        isFundraiser: false,
        formOrganization: organization,
        isAuction,
        isShareModalOpen,
        openShareModal,
        closeShareModal,
        formCategory,
        logAmplitudeOnSubmitFailure,
        logAmplitudeOnSubmitSuccess,
        eventFirstStartDate,
      }}
    >
      {children}
    </TicketingFormEditionContext.Provider>
  );
};
